import { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Box, Text, Skeleton } from '@machobear/design-system'
import { lakshmi } from '@machobear/design-system/themes/lakshmi'
import { colors } from '@/styles'
import { useSidebarContext } from '@/hooks/use-sidebar-context'
import { EquifaxLogomark } from '@/components/icons'

const INDICATOR_WIDTH = 14
const NUMBER_OF_SECTIONS = 5

const Indicator = styled.div`
  position: relative;
  width: ${INDICATOR_WIDTH}px;
  height: 28px;
  bottom: 7px;
  border: ${props => `3px solid ${props.color}`};
  filter: drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.25)) drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.05));
  background-color: #fff;
  border-radius: 20px;
`

const CreditSummaryBar = ({ data, isLocked, isLoading }) => {
  const [width, setWidth] = useState(0)
  const [position, setPosition] = useState(0)
  const { isSidebarOpen } = useSidebarContext()
  const range = data?.credit_score_range
  const score = range?.slice(-3)
  const ref = useRef(null)

  const displayDisabled = isLocked || score < 300

  const rangeSliderTones = {
    poor: lakshmi.color.background.critical,
    fair: colors.fair,
    good: lakshmi.color.background.caution,
    veryGood: colors.veryGood,
    excellent: lakshmi.color.background.positive
  }

  const getCreditRate = score => {
    if (score < 580) {
      return 'poor'
    } else if (score < 670) {
      return 'fair'
    } else if (score < 740) {
      return 'good'
    } else if (score < 800) {
      return 'very_good'
    } else {
      return 'excellent'
    }
  }

  const getStartingPoint = rate => {
    switch (rate) {
      case 'poor':
        return { block: 0, range: 280, start: 300 }
      case 'fair':
        return { block: 1, range: 90, start: 580 }
      case 'good':
        return { block: 2, range: 70, start: 670 }
      case 'very_good':
        return { block: 3, range: 60, start: 740 }
      case 'excellent':
        return { block: 4, range: 100, start: 800 }
      default:
        return { block: 0, range: 280, start: 300 }
    }
  }

  const calculateSliderPosition = () => {
    const startingPoint = getStartingPoint(getCreditRate(score))
    const delta = (score - startingPoint.start) / startingPoint.range
    return (startingPoint.block + delta) / NUMBER_OF_SECTIONS - 0.01 // -1% to account for slider width
  }

  const handleResize = () => {
    setTimeout(() => {
      if (ref.current) {
        const newWidth = ref.current.getBoundingClientRect().width
        setWidth(newWidth)
      }
    }, 150) // Delay execution to ensure transitions have completed
  }

  useEffect(() => {
    if (displayDisabled) {
      setPosition(0)
    } else {
      setPosition(calculateSliderPosition())
    }
  }, [data, width])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    handleResize()
  }, [isSidebarOpen])

  useEffect(() => {
    setTimeout(() => {
      handleResize()
    }, 2000)
  }, [])

  const getIndicatorColor = () => {
    if (score < 580) {
      return rangeSliderTones.poor
    } else if (score < 670) {
      return rangeSliderTones.fair
    } else if (score < 740) {
      return rangeSliderTones.good
    } else if (score < 800) {
      return rangeSliderTones.veryGood
    } else {
      return rangeSliderTones.excellent
    }
  }

  return (
    <Box
      paddingX={{ mobile: 'xxsmall', tablet: 'gutter' }}
      paddingY="gutter"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      borderRadius="small"
      border="borderNeutralLight"
      gap="large"
      marginBottom="gutter"
    >
      <Box display="flex" alignItems="center" gap="xxxsmall">
        <EquifaxLogomark />
        <Text weight="strong">Credit Score:</Text>
        <Box
          style={{
            fontWeight: '700',
            color: isLocked || isLoading ? 'black' : getIndicatorColor()
          }}
        >
          {displayDisabled || isLoading ? '--' : range}
        </Box>
      </Box>
      <Box
        width="full"
        display="flex"
        paddingBottom="xsmall"
        position="relative"
        flexDirection="column"
        ref={ref}
        gap="xxsmall"
      >
        <Box
          width="full"
          display="flex"
          alignItems="center"
          justifyContent="spaceBetween"
          marginBottom="xsmall"
        >
          <Box position="absolute" style={{ left: '0%', width: '20%' }}>
            <Text tone="tertiary" size="xsmall">
              Poor
            </Text>
          </Box>
          <Box position="absolute" style={{ left: '20%', width: '20%' }}>
            <Text tone="tertiary" size="xsmall">
              Fair
            </Text>
          </Box>
          <Box position="absolute" style={{ left: '40%', width: '20%' }}>
            <Text tone="tertiary" size="xsmall">
              Good
            </Text>
          </Box>
          <Box position="absolute" style={{ left: '60%', width: '20%' }}>
            <Text tone="tertiary" size="xsmall">
              Very Good
            </Text>
          </Box>
          <Box position="absolute" style={{ left: '80%', width: '20%' }}>
            <Text tone="tertiary" size="xsmall">
              Excellent
            </Text>
          </Box>
        </Box>

        {isLoading ? (
          <Skeleton.Shape height={15} width={'100%'} borderRadius="full" />
        ) : (
          <Box
            background="brandAccentSoft"
            display="flex"
            borderRadius="xlarge"
            overflow="hidden"
            gap="xxxsmall"
          >
            <Box position="absolute" style={{ left: `${position * 100}%`, zIndex: 2 }}>
              {!displayDisabled && <Indicator color={getIndicatorColor()} />}
            </Box>

            <Box
              background="white"
              style={{
                width: `${(1 - position) * 100}%`,
                height: '15px',
                position: 'absolute',
                opacity: '0.8',
                right: '0'
              }}
            />

            <Box background="critical" style={{ height: '15px' }} display="flex" width="full" />
            <Box
              style={{ height: '15px', background: rangeSliderTones.fair }}
              display="flex"
              width="full"
            />
            <Box background="caution" style={{ height: '15px' }} display="flex" width="full" />
            <Box
              style={{ height: '15px', background: rangeSliderTones.veryGood }}
              display="flex"
              width="full"
            />
            <Box background="positive" style={{ height: '15px' }} display="flex" width="full" />
          </Box>
        )}

        <Box width="full" paddingTop="xxsmall" display="flex" justifyContent="spaceBetween">
          <Box display="flex" justifyContent="flexEnd">
            <Text size="xsmall" tone="tertiary">
              300
            </Text>
          </Box>
          <Box display="flex" justifyContent="flexEnd" position="absolute" style={{ left: '20%' }}>
            <Text size="xsmall" tone="tertiary">
              580
            </Text>
          </Box>
          <Box display="flex" justifyContent="flexEnd" position="absolute" style={{ left: '40%' }}>
            <Text size="xsmall" tone="tertiary">
              670
            </Text>
          </Box>
          <Box display="flex" justifyContent="flexEnd" position="absolute" style={{ left: '60%' }}>
            <Text size="xsmall" tone="tertiary">
              740
            </Text>
          </Box>
          <Box display="flex" justifyContent="flexEnd" position="absolute" style={{ left: '80%' }}>
            <Text size="xsmall" tone="tertiary">
              800
            </Text>
          </Box>
          <Box display="flex" justifyContent="flexEnd">
            <Text size="xsmall" tone="tertiary">
              900
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default CreditSummaryBar
